import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { getUserInfo, submitContactUs } from "../../services/User/Authentication";

function readStoredUser() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return {};
  }
  try {
    return JSON.parse(storedUser);
  } catch {
    return {};
  }
}

export default function HelpPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(readStoredUser());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      try {
        const userInfo = await getUserInfo();
        setUser((currentUser) => {
          const nextUser = { ...currentUser, ...userInfo };
          localStorage.setItem("user", JSON.stringify(nextUser));
          if (nextUser.subscription) {
            localStorage.setItem("subscription", JSON.stringify(nextUser.subscription));
          } else {
            localStorage.removeItem("subscription");
          }
          return nextUser;
        });
      } catch (err) {
        console.error("Failed to load user info:", err);
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (user) {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
      setName(fullName || user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("All fields are required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await submitContactUs({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      toast.success("Your inquiry has been sent successfully!");
      setMessage("");
    } catch (err) {
      toast.error(err.error || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar />

      <div className="flex justify-center items-center px-5 lg:py-12 py-8">
      <main className="max-w-[1280px] w-full">
        <button
          onClick={() => {
            if (window.history.length > 2) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
          className="flex items-center gap-1.5 appearance-none border-none bg-transparent xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-semibold cursor-pointer md:mb-6 mb-4"
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 20 }} /> Back to Home
        </button>

        <section className="grid lg:grid-cols-[minmax(0,1.3fr)_minmax(320px,450px)] grid-cols-1 items-start lg:gap-12 gap-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <p className="text-[var(--color-text-muted)] xl:text-sm text-xs font-bold uppercase tracking-[0.08em]">Help Center</p>
              <h1 className="max-w-3xl xl:text-5xl md:text-4xl text-[32px] leading-[1.05] font-bold tracking-[-0.03em]">The FormGuru Support</h1>
              <p className="max-w-2xl text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium">
                The FormGuru is the ultimate horse race prediction and analytics app and website, providing automated, state-of-the-art predictive insights and dynamic ratings to help users form-analyze horse racing events with precision.
              </p>
              <p className="max-w-2xl text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium">
                Need help with your account, billing, or the app or website? Contact our support team directly at <a href="mailto:support@formguru.com.au" className="text-[var(--color-primary)]">support@formguru.com.au</a> or submit the form on this page.
              </p>
            </div>

            <div className="flex gap-4 border-y border-[var(--color-border)] py-4">
              <a href="/terms" className="text-[var(--color-primary)] xl:text-base md:text-sm text-xs font-semibold">Terms of Use</a>
              <span className="text-[var(--color-text-muted)]">|</span>
              <a href="/privacy" className="text-[var(--color-primary)] xl:text-base md:text-sm text-xs font-semibold">Privacy Policy</a>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Account & Login
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  Log in securely using your registered email and password. If you forget your password, use the "Forgot Password" feature on the login screen of the app or website to request a reset code.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Subscription & Billing
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  Manage premium features, upgrade tiers, or claim free trials directly in the App's Subscription panel or website portal. Standard transactions are billed securely via Apple App Store or Google Play Store.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Notifications
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  Receive real-time push alerts for predictions, results, and tips. Ensure push notifications are enabled both inside the app or website settings and your phone's native Settings app under "The FormGuru".
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Apple & Google Sign-In
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  Use one-click social authentication to securely create or access your profile. If you run into issues, verify that you have granted permission to The FormGuru in your Apple ID or Google Account settings.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Account Deletion
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  We value your privacy. You can permanently delete your account and all associated personal data at any time. Go to <strong>Profile &gt; Delete Account</strong> inside the The FormGuru app or website portal, or request account deletion by submitting the support form on this page with your registered email.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em]">
                  Technical Issues
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                  If the app or website experiences network lag or prediction data fails to load, try force-closing and restarting the app, refreshing the website, clearing cache, or updating to the latest version in the App Store.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 w-full lg:sticky lg:top-36">
            <div className="flex flex-col gap-3 mb-6">
              <h2 className="xl:text-[22px] md:text-xl text-lg">Contact Support</h2>
              <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium">Send an inquiry directly to our team</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-[20px]">
              <div>
                <label className="md:text-sm text-xs label-caps block mb-2">
                  Name
                </label>
                <div className="relative flex items-center">
                  <PersonRoundedIcon className="absolute left-[14px] text-[var(--color-text-muted)]" sx={{ fontSize: 20 }} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full py-3 pl-10 pr-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="md:text-sm text-xs label-caps block mb-2">
                  Email Address
                </label>
                <div className="relative flex items-center">
                  <MailOutlineRoundedIcon className="absolute left-[14px] text-[var(--color-text-muted)]" sx={{ fontSize: 20 }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full py-3 pl-10 pr-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="md:text-sm text-xs label-caps block mb-2">
                  Message
                </label>
                <div className="relative flex items-start">
                  <ChatBubbleOutlineRoundedIcon className="absolute left-[14px] top-[14px] text-[var(--color-text-muted)]" sx={{ fontSize: 20 }} />
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here..."
                    rows={5}
                    className="w-full py-3 pl-10 pr-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`appearance-none md:w-auto w-full xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] cursor-pointer ${loading ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                {loading ? "Sending..." : "Submit Inquiry"}
              </button>
            </form>
          </div>
        </section>
      </main>
      </div>

      <Footer />
    </div>
  );
}
